// src/components/learning/Learning.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { auth } from '../../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  ArrowLeft, Search, HelpCircle, MessageCircle, Play, Eye, Star, Users,
  X, Send, ChevronDown, RefreshCw, Video, Clock, ThumbsUp,
  Lightbulb, BookOpen, Award, TrendingUp, CheckCircle, Loader
} from 'lucide-react';
import './Learning.css';

// ============================================================
// TYPES
// ============================================================
interface LearningVideo {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  video_url_1: string | null;
  video_url_2: string | null;
  video_url_3: string | null;
  video_url_4: string | null;
  video_url_5: string | null;
  thumbnail_url: string | null;
  views_count: number;
  total_stars: number;
  total_reviews: number;
  average_rating: number;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LearningSearchHistory {
  id: string;
  user_id: string;
  search_term: string;
  created_at: string;
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const parseNotes = (notesString: string | null): string[] => {
  if (!notesString) return [];
  return notesString
    .split('#')
    .map(note => note.trim())
    .filter(note => note.length > 0);
};

const formatNotes = (notes: string[]): string => {
  if (notes.length === 0) return '';
  return '#' + notes.join('#');
};

const getVideoUrl = (video: LearningVideo): string | null => {
  // Priority: uploaded video > external URLs
  if (video.video_url) return video.video_url;
  if (video.video_url_1) return video.video_url_1;
  if (video.video_url_2) return video.video_url_2;
  if (video.video_url_3) return video.video_url_3;
  if (video.video_url_4) return video.video_url_4;
  if (video.video_url_5) return video.video_url_5;
  return null;
};

const getAllVideoUrls = (video: LearningVideo): { label: string; url: string }[] => {
  const urls: { label: string; url: string }[] = [];
  if (video.video_url) urls.push({ label: 'Uploaded Video', url: video.video_url });
  if (video.video_url_1) urls.push({ label: 'Video Link 1', url: video.video_url_1 });
  if (video.video_url_2) urls.push({ label: 'Video Link 2', url: video.video_url_2 });
  if (video.video_url_3) urls.push({ label: 'Video Link 3', url: video.video_url_3 });
  if (video.video_url_4) urls.push({ label: 'Video Link 4', url: video.video_url_4 });
  if (video.video_url_5) urls.push({ label: 'Video Link 5', url: video.video_url_5 });
  return urls;
};

const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be');
};

const getYouTubeEmbedUrl = (url: string): string => {
  let videoId = '';
  if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
  } else if (url.includes('youtube.com/watch')) {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    videoId = urlParams.get('v') || '';
  } else if (url.includes('youtube.com/embed/')) {
    videoId = url.split('youtube.com/embed/')[1]?.split('?')[0] || '';
  }
  return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : url;
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const Learning: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [videos, setVideos] = useState<LearningVideo[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<LearningVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  // Search state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null);
  
  // Video modal state
  const [selectedVideo, setSelectedVideo] = useState<LearningVideo | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState<number | null>(null);
  const [hasCountedView, setHasCountedView] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  
  // Note modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  
  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const viewTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // ============================================================
  // AUTH STATE
  // ============================================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);
  
  // ============================================================
  // LOAD VIDEOS
  // ============================================================
  const loadVideos = useCallback(async () => {
    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('learning_videos')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      setVideos(data || []);
      setFilteredVideos(data || []);
    } catch (err: any) {
      console.error('Error loading videos:', err);
      setError('Failed to load learning videos');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // ============================================================
  // LOAD SEARCH HISTORY
  // ============================================================
  const loadSearchHistory = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const { data, error: fetchError } = await supabase
        .from('learning_search_history')
        .select('search_term')
        .eq('user_id', currentUser.uid)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (fetchError) throw fetchError;
      
      const uniqueTerms = Array.from(new Set((data || []).map(d => d.search_term)));
      setSearchHistory(uniqueTerms);
    } catch (err) {
      console.error('Error loading search history:', err);
    }
  }, [currentUser]);
  
  // ============================================================
  // INITIAL LOAD
  // ============================================================
  useEffect(() => {
    loadVideos();
  }, [loadVideos]);
  
  useEffect(() => {
    if (currentUser) {
      loadSearchHistory();
    }
  }, [currentUser, loadSearchHistory]);
  
  // ============================================================
  // SEARCH FUNCTIONALITY
  // ============================================================
  const saveSearchHistory = async (term: string) => {
    if (!currentUser || !term.trim()) return;
    
    try {
      await supabase.from('learning_search_history').insert({
        user_id: currentUser.uid,
        search_term: term.trim().toLowerCase()
      });
      await loadSearchHistory();
    } catch (err) {
      console.error('Error saving search history:', err);
    }
  };
  
  const performSearch = async (term: string) => {
    const q = term.trim();
    if (!q) {
      setFilteredVideos(videos);
      setActiveSearchTerm(null);
      return;
    }
    
    setIsSearching(true);
    setActiveSearchTerm(q);
    
    try {
      // Save to history
      await saveSearchHistory(q);
      
      // Search in title and description
      const { data, error: searchError } = await supabase
        .from('learning_videos')
        .select('*')
        .eq('is_active', true)
        .or(`title.ilike.%${q}%,description.ilike.%${q}%`)
        .order('created_at', { ascending: false });
      
      if (searchError) throw searchError;
      
      setFilteredVideos(data || []);
    } catch (err) {
      console.error('Search error:', err);
      setFilteredVideos([]);
    } finally {
      setIsSearching(false);
      setShowSearchModal(false);
    }
  };
  
  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearchTerm(null);
    setFilteredVideos(videos);
  };
  
  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      performSearch(searchQuery);
    }
  };
  
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };
  
  const handleHistoryClick = (term: string) => {
    setSearchQuery(term);
    performSearch(term);
  };
  
  // ============================================================
  // VIDEO MODAL HANDLERS
  // ============================================================
  const openVideoModal = (video: LearningVideo) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
    setVideoStartTime(Date.now());
    setHasCountedView(false);
    setIsPlaying(false);
  };
  
  const closeVideoModal = () => {
    // Clear view timer
    if (viewTimerRef.current) {
      clearTimeout(viewTimerRef.current);
      viewTimerRef.current = null;
    }
    
    // Show rating modal instead of just closing
    setShowVideoModal(false);
    setShowRatingModal(true);
    setIsPlaying(false);
  };
  
  const incrementViewCount = async (videoId: string) => {
    try {
      // Get current view count
      const { data: currentVideo } = await supabase
        .from('learning_videos')
        .select('views_count')
        .eq('id', videoId)
        .single();
      
      if (currentVideo) {
        await supabase
          .from('learning_videos')
          .update({ 
            views_count: (currentVideo.views_count || 0) + 1,
            updated_at: new Date().toISOString()
          })
          .eq('id', videoId);
        
        // Update local state
        setVideos(prev => prev.map(v => 
          v.id === videoId ? { ...v, views_count: (v.views_count || 0) + 1 } : v
        ));
        setFilteredVideos(prev => prev.map(v => 
          v.id === videoId ? { ...v, views_count: (v.views_count || 0) + 1 } : v
        ));
      }
    } catch (err) {
      console.error('Error incrementing view count:', err);
    }
  };
  
  // View timer - increment after 5 seconds
  useEffect(() => {
    if (showVideoModal && selectedVideo && !hasCountedView) {
      viewTimerRef.current = setTimeout(() => {
        incrementViewCount(selectedVideo.id);
        setHasCountedView(true);
      }, 5000);
    }
    
    return () => {
      if (viewTimerRef.current) {
        clearTimeout(viewTimerRef.current);
      }
    };
  }, [showVideoModal, selectedVideo, hasCountedView]);
  
  // ============================================================
  // RATING HANDLERS
  // ============================================================
  const submitRating = async (rating: number) => {
    if (!selectedVideo || isSubmittingRating) return;
    
    setIsSubmittingRating(true);
    setSelectedRating(rating);
    
    try {
      const newTotalStars = (selectedVideo.total_stars || 0) + rating;
      const newTotalReviews = (selectedVideo.total_reviews || 0) + 1;
      const newAverageRating = newTotalStars / newTotalReviews;
      
      const { error: updateError } = await supabase
        .from('learning_videos')
        .update({
          total_stars: newTotalStars,
          total_reviews: newTotalReviews,
          average_rating: Math.round(newAverageRating * 100) / 100,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedVideo.id);
      
      if (updateError) throw updateError;
      
      // Update local state
      setVideos(prev => prev.map(v => 
        v.id === selectedVideo.id 
          ? { 
              ...v, 
              total_stars: newTotalStars, 
              total_reviews: newTotalReviews,
              average_rating: Math.round(newAverageRating * 100) / 100
            } 
          : v
      ));
      setFilteredVideos(prev => prev.map(v => 
        v.id === selectedVideo.id 
          ? { 
              ...v, 
              total_stars: newTotalStars, 
              total_reviews: newTotalReviews,
              average_rating: Math.round(newAverageRating * 100) / 100
            } 
          : v
      ));
      
      // Move to note modal
      setShowRatingModal(false);
      setShowNoteModal(true);
    } catch (err) {
      console.error('Error submitting rating:', err);
    } finally {
      setIsSubmittingRating(false);
    }
  };
  
  const skipRating = () => {
    setShowRatingModal(false);
    setShowNoteModal(true);
  };
  
  // ============================================================
  // NOTE HANDLERS
  // ============================================================
  const submitNote = async () => {
    if (!selectedVideo || !noteText.trim() || isSubmittingNote) {
      closeNoteModal();
      return;
    }
    
    setIsSubmittingNote(true);
    
    try {
      const existingNotes = parseNotes(selectedVideo.notes);
      const newNotes = [...existingNotes, noteText.trim()];
      const formattedNotes = formatNotes(newNotes);
      
      const { error: updateError } = await supabase
        .from('learning_videos')
        .update({
          notes: formattedNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedVideo.id);
      
      if (updateError) throw updateError;
      
      // Update local state
      setVideos(prev => prev.map(v => 
        v.id === selectedVideo.id ? { ...v, notes: formattedNotes } : v
      ));
      setFilteredVideos(prev => prev.map(v => 
        v.id === selectedVideo.id ? { ...v, notes: formattedNotes } : v
      ));
      
      closeNoteModal();
    } catch (err) {
      console.error('Error submitting note:', err);
    } finally {
      setIsSubmittingNote(false);
    }
  };
  
  const closeNoteModal = () => {
    setShowNoteModal(false);
    setNoteText('');
    setSelectedVideo(null);
    setSelectedRating(null);
  };
  
  // ============================================================
  // VIDEO PLAYER RENDER
  // ============================================================
  const renderVideoPlayer = () => {
    if (!selectedVideo) return null;
    
    const videoUrl = getVideoUrl(selectedVideo);
    if (!videoUrl) {
      return (
        <div className="learning-video-placeholder">
          <Video size={64} />
          <p>No video available</p>
        </div>
      );
    }
    
    if (isYouTubeUrl(videoUrl)) {
      return (
        <iframe
          className="learning-video-iframe"
          src={getYouTubeEmbedUrl(videoUrl)}
          title={selectedVideo.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    
    return (
      <video
        className="learning-video-player"
        src={videoUrl}
        controls
        autoPlay={isPlaying}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
    );
  };
  
  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="learning">
      {/* ====================================================
          HEADER (FIXED)
          ==================================================== */}
      <header className="learning-header">
        <button 
          className="learning-header-back"
          onClick={() => navigate(-1)}
          title="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        
        <div className="learning-header-title">
          <Lightbulb size={20} />
          <span>Learning</span>
        </div>
        
        <div className="learning-header-actions">
          <button 
            className="learning-header-btn"
            onClick={() => {
              setShowSearchModal(true);
              setTimeout(() => searchInputRef.current?.focus(), 100);
            }}
            title="Search"
          >
            <Search size={18} />
          </button>
          <button 
            className="learning-header-btn"
            onClick={() => navigate('/feedbacks')}
            title="FAQ"
          >
            <HelpCircle size={18} />
          </button>
          <button 
            className="learning-header-btn"
            onClick={() => window.open('https://wa.me/2349134081226', '_blank')}
            title="Contact Us"
          >
            <MessageCircle size={18} />
          </button>

          <button 
            className="learning-header-btn"
            onClick={() => navigate('/openmarket')}
            title="Contact Us"
          >
            <Users size={18} />
          </button>
        </div>
      </header>
      
      {/* ====================================================
          DESCRIPTION (SCROLLABLE WITH BODY)
          ==================================================== */}
      <div className="learning-description">
        <h2>Welcome back! 👋</h2>
        <p>Keep on maximizing GoStorez's potential. Watch tutorials, learn tips, and grow your business.</p>
      </div>
      
      {/* Active search banner */}
      {activeSearchTerm && (
        <div className="learning-search-banner">
          <span>Results for "{activeSearchTerm}"</span>
          <button onClick={clearSearch}>
            <X size={14} /> Clear
          </button>
        </div>
      )}
      
      {/* ====================================================
          BODY - VIDEO LIST
          ==================================================== */}
      <main className="learning-body">
        {loading ? (
          <div className="learning-loading">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading tutorials...</p>
          </div>
        ) : error ? (
          <div className="learning-error">
            <p>{error}</p>
            <button onClick={loadVideos}>
              <RefreshCw size={16} /> Retry
            </button>
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="learning-empty">
            <BookOpen size={48} />
            <h3>No tutorials found</h3>
            <p>
              {activeSearchTerm 
                ? 'Try a different search term' 
                : 'Check back soon for new content'}
            </p>
            {activeSearchTerm && (
              <button onClick={clearSearch}>Clear Search</button>
            )}
          </div>
        ) : (
          <div className="learning-video-list">
            {filteredVideos.map(video => {
              const videoUrl = getVideoUrl(video);
              const thumbnailSrc = video.thumbnail_url || null;
              
              return (
                <div 
                  key={video.id} 
                  className="learning-video-card"
                  onClick={() => openVideoModal(video)}
                >
                  <div className="learning-video-thumbnail">
                    {thumbnailSrc ? (
                      <img 
                        src={thumbnailSrc} 
                        alt={video.title}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="learning-video-thumbnail-placeholder">
                        <Video size={32} />
                      </div>
                    )}
                    <div className="learning-video-play-overlay">
                      <Play size={24} fill="white" />
                    </div>
                  </div>
                  
                  <div className="learning-video-info">
                    <h3 className="learning-video-title">{video.title}</h3>
                    <p className="learning-video-desc">
                      {video.description?.substring(0, 100)}
                      {video.description && video.description.length > 100 ? '...' : ''}
                    </p>
                    <div className="learning-video-stats">
                      <span className="learning-stat">
                        <Eye size={12} /> {formatNumber(video.views_count || 0)} views
                      </span>
                      <span className="learning-stat">
                        <Star size={12} /> {(video.average_rating || 0).toFixed(1)} ({(video.total_reviews || 0)})
                      </span>
                      <span className="learning-stat">
                        <Clock size={12} /> {formatDate(video.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      
      {/* ====================================================
          FOOTER
          ==================================================== */}
      <footer className="learning-footer">
        <p>MyGostorez 💝</p>
        <p className="learning-footer-tagline">• Have fun • Connect • Earn</p>
      </footer>
      
      {/* ====================================================
          SEARCH MODAL
          ==================================================== */}
      {showSearchModal && (
        <div className="learning-modal-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="learning-search-modal" onClick={e => e.stopPropagation()}>
            <div className="learning-search-header">
              <h3>Search Tutorials</h3>
              <button onClick={() => setShowSearchModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="learning-search-input-wrapper">
              <Search size={18} className="learning-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by title or description..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
              />
              <button 
                className="learning-search-go"
                onClick={handleSearchSubmit}
                disabled={!searchQuery.trim() || isSearching}
              >
                {isSearching ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
            
            {searchHistory.length > 0 && (
              <div className="learning-search-history">
                <h4>Recent Searches</h4>
                <div className="learning-search-history-list">
                  {searchHistory.map((term, idx) => (
                    <button
                      key={`${term}_${idx}`}
                      className="learning-search-history-item"
                      onClick={() => handleHistoryClick(term)}
                    >
                      <Clock size={12} />
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ====================================================
          VIDEO MODAL
          ==================================================== */}
      {showVideoModal && selectedVideo && (
        <div className="learning-video-modal">
          {/* Video Player - Top 50% */}
          <div className="learning-video-modal-player">
            {renderVideoPlayer()}
            <button 
              className="learning-video-modal-close"
              onClick={closeVideoModal}
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Details - Bottom 50% */}
          <div className="learning-video-modal-details">
            <div className="learning-video-modal-header">
              <button 
                className="learning-video-modal-back"
                onClick={closeVideoModal}
              >
                <ArrowLeft size={20} />
              </button>
              <h2>{selectedVideo.title}</h2>
            </div>
            
            <div className="learning-video-modal-stats">
              <span><Eye size={14} /> {formatNumber(selectedVideo.views_count || 0)} views</span>
              <span><Star size={14} /> {(selectedVideo.average_rating || 0).toFixed(1)} rating</span>
              <span><ThumbsUp size={14} /> {selectedVideo.total_reviews || 0} reviews</span>
            </div>
            
            <div className="learning-video-modal-description">
              <h4>Description</h4>
              <p>{selectedVideo.description || 'No description available.'}</p>
            </div>
            
            {/* All Video Links */}
            <div className="learning-video-modal-links">
              <h4>Video Links</h4>
              {getAllVideoUrls(selectedVideo).map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="learning-video-link"
                >
                  <Play size={14} />
                  {link.label}
                </a>
              ))}
              {getAllVideoUrls(selectedVideo).length === 0 && (
                <p className="learning-no-links">No video links available</p>
              )}
            </div>
            
            {/* Notes */}
            {selectedVideo.notes && parseNotes(selectedVideo.notes).length > 0 && (
              <div className="learning-video-modal-notes">
                <h4>Community Notes</h4>
                <ul>
                  {parseNotes(selectedVideo.notes).map((note, idx) => (
                    <li key={idx}>{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ====================================================
          RATING MODAL
          ==================================================== */}
      {showRatingModal && (
        <div className="learning-modal-overlay">
          <div className="learning-rating-modal">
            <div className="learning-rating-header">
              <button 
                className="learning-rating-back"
                onClick={skipRating}
              >
                <ArrowLeft size={20} />
              </button>
              <h3>Was this helpful?</h3>
            </div>
            
            <div className="learning-rating-options">
              <button 
                className={`learning-rating-option ${selectedRating === 5 ? 'selected' : ''}`}
                onClick={() => submitRating(5)}
                disabled={isSubmittingRating}
              >
                <span className="learning-rating-label">Perfect✨</span>
                <span className="learning-rating-stars">⭐⭐⭐⭐⭐</span>
              </button>
              
              <button 
                className={`learning-rating-option ${selectedRating === 4 ? 'selected' : ''}`}
                onClick={() => submitRating(4)}
                disabled={isSubmittingRating}
              >
                <span className="learning-rating-label">Gooood!</span>
                <span className="learning-rating-stars">⭐⭐⭐⭐</span>
              </button>
              
              <button 
                className={`learning-rating-option ${selectedRating === 3 ? 'selected' : ''}`}
                onClick={() => submitRating(3)}
                disabled={isSubmittingRating}
              >
                <span className="learning-rating-label">Average</span>
                <span className="learning-rating-stars">⭐⭐⭐</span>
              </button>
              
              <button 
                className={`learning-rating-option ${selectedRating === 2 ? 'selected' : ''}`}
                onClick={() => submitRating(2)}
                disabled={isSubmittingRating}
              >
                <span className="learning-rating-label">Barely</span>
                <span className="learning-rating-stars">⭐⭐</span>
              </button>
              
              <button 
                className={`learning-rating-option ${selectedRating === 1 ? 'selected' : ''}`}
                onClick={() => submitRating(1)}
                disabled={isSubmittingRating}
              >
                <span className="learning-rating-label">Not helping</span>
                <span className="learning-rating-stars">⭐</span>
              </button>
            </div>
            
            {isSubmittingRating && (
              <div className="learning-rating-submitting">
                <Loader size={20} className="animate-spin" />
                <span>Submitting...</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* ====================================================
          NOTE MODAL
          ==================================================== */}
      {showNoteModal && (
        <div className="learning-modal-overlay">
          <div className="learning-note-modal">
            <div className="learning-note-header">
              <h3>Leave a bothering note</h3>
            </div>
            
            <div className="learning-note-body">
              <textarea
                placeholder="Share your thoughts, suggestions, or concerns..."
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                rows={5}
              />
            </div>
            
            <div className="learning-note-actions">
              <button 
                className="learning-note-skip"
                onClick={closeNoteModal}
              >
                Skip
              </button>
              <button 
                className="learning-note-submit"
                onClick={submitNote}
                disabled={isSubmittingNote}
              >
                {isSubmittingNote ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Done
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Learning;